import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Check, Sparkles, ArrowRight, Zap, Building2, Rocket, Wallet, ArrowDownToLine, Clock, Globe, ShieldCheck, Banknote, Timer, HeadphonesIcon } from 'lucide-react';

const PlanCard = ({ plan, index, accent = 'primary', t }) => {
  const Icon = plan.icon;
  const accentVar = accent === 'accent' ? '--neon-accent' : '--primary';
  const accentGlowVar = accent === 'accent' ? '--neon-accent' : '--primary-glow';

  return (
    <Card
      data-testid={`pricing-card-${accent}-${index}`}
      className={`
        relative p-6 sm:p-8 bg-[hsl(var(--surface)/0.9)] border-[hsl(var(${accentGlowVar})/0.2)]
        transition-all duration-300 hover-lift group
        ${plan.popular
          ? `mt-5 border-[hsl(var(${accentGlowVar})/0.6)] shadow-[0_0_50px_hsl(var(${accentVar})/0.2)]`
          : `hover:border-[hsl(var(${accentVar})/0.4)] shadow-[0_0_15px_hsl(var(${accentVar})/0.08)]`
        }
      `}
    >
      {plan.popular && (
        <>
          <div className={`absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[hsl(var(${accentGlowVar}))] to-transparent`} />
          <div className={`absolute -top-20 left-1/2 -translate-x-1/2 w-60 h-40 bg-[hsl(var(${accentVar}))] opacity-[0.15] blur-[60px] rounded-full`} />
          <Badge
            data-testid={`popular-badge-${accent}`}
            className={`absolute -top-3 left-1/2 -translate-x-1/2 text-white border-0 ${
              accent === 'accent'
                ? 'bg-gradient-to-r from-[hsl(var(--neon-accent))] to-[hsl(var(--primary))] shadow-[0_0_25px_hsl(var(--neon-accent)/0.6)]'
                : 'bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--neon-accent))] shadow-[0_0_25px_hsl(var(--primary)/0.6)]'
            }`}
          >
            <Sparkles className="w-3 h-3 mr-1" /> {t('pricing.most_popular')}
          </Badge>
        </>
      )}
      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-5">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
            plan.popular ? `bg-[hsl(var(${accentVar})/0.25)] shadow-[0_0_20px_hsl(var(${accentVar})/0.3)]` : 'bg-[hsl(var(--surface-elevated))]'
          }`}>
            <Icon className={`w-6 h-6 ${plan.popular ? `text-[hsl(var(${accentGlowVar}))] drop-shadow-[0_0_8px_hsl(var(${accentGlowVar}))]` : 'text-white/60'}`} />
          </div>
          <h3 className="text-2xl font-bold text-white">{plan.name}</h3>
        </div>
        <div className="mb-2">
          <span className={`text-3xl sm:text-4xl font-extrabold ${
            plan.popular
              ? accent === 'accent' ? 'text-gradient-accent text-neon-accent' : 'text-gradient text-neon'
              : 'text-white'
          }`}>{plan.price}</span>
          <p className="text-sm text-white/65 mt-1">{plan.description}</p>
        </div>
        {plan.highlight && (
          <div className={`inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-lg ${
            accent === 'accent'
              ? 'bg-[hsl(var(--neon-accent)/0.12)] border border-[hsl(var(--neon-accent)/0.3)]'
              : 'bg-[hsl(var(--primary)/0.12)] border border-[hsl(var(--primary)/0.3)]'
          }`}>
            <Clock className={`w-3.5 h-3.5 ${accent === 'accent' ? 'text-[hsl(var(--neon-accent))]' : 'text-[hsl(var(--primary-glow))]'}`} />
            <span className={`text-xs font-semibold ${accent === 'accent' ? 'text-[hsl(var(--neon-accent))]' : 'text-[hsl(var(--primary-glow))]'}`}>
              {plan.highlightLabel}: <span className="text-white font-bold">{plan.highlight}</span>
            </span>
          </div>
        )}
        <ul className={`space-y-3 ${plan.highlight ? 'mb-6' : 'mb-8'}`}>
          {plan.features.map((feature, fi) => (
            <li key={fi} className="flex items-start gap-3 text-sm" data-testid={`plan-feature-${accent}-${index}-${fi}`}>
              <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                plan.popular ? `bg-[hsl(var(${accentVar})/0.25)]` : 'bg-[hsl(var(--surface-elevated))]'
              }`}>
                <Check className={`h-3 w-3 ${plan.popular ? `text-[hsl(var(${accentGlowVar}))] drop-shadow-[0_0_4px_hsl(var(${accentGlowVar}))]` : 'text-white/60'}`} />
              </div>
              <span className="text-white/85">{feature}</span>
            </li>
          ))}
        </ul>
        <Button
          data-testid={`pricing-cta-${accent}-${index}`}
          className={`w-full h-12 gap-2 group/btn text-base font-semibold ${
            plan.popular
              ? accent === 'accent'
                ? 'btn-neon-accent shadow-[0_0_25px_hsl(var(--neon-accent)/0.4)]'
                : 'btn-neon shadow-[0_0_25px_hsl(var(--primary)/0.4)]'
              : 'bg-[hsl(var(--surface-elevated))] text-white/80 hover:text-white border border-[hsl(var(--border)/0.5)] hover:border-[hsl(var(--primary)/0.5)] hover:shadow-[0_0_20px_hsl(var(--primary)/0.2)]'
          }`}
          variant={plan.popular ? 'default' : 'ghost'}
        >
          {plan.cta}
          <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover/btn:translate-x-1" />
        </Button>
      </div>
    </Card>
  );
};

export const Pricing = () => {
  const { t } = useTranslation();

  const cryptoPlans = [
    { name: 'Starter', icon: Rocket, price: '2.5%', description: t('pricing.per_tx'), features: [t('pricing.crypto_s_feat1'), t('pricing.crypto_s_feat2'), t('pricing.crypto_s_feat3'), t('pricing.crypto_s_feat4'), t('pricing.crypto_s_feat5')], cta: t('pricing.crypto_s_cta'), popular: false },
    { name: 'Pro', icon: Zap, price: '1.5%', description: t('pricing.per_tx'), features: [t('pricing.crypto_p_feat1'), t('pricing.crypto_p_feat2'), t('pricing.crypto_p_feat3'), t('pricing.crypto_p_feat4'), t('pricing.crypto_p_feat5')], cta: t('pricing.crypto_p_cta'), popular: true },
    { name: 'Enterprise', icon: Building2, price: 'Custom', description: t('pricing.high_volume'), features: [t('pricing.crypto_e_feat1'), t('pricing.crypto_e_feat2'), t('pricing.crypto_e_feat3'), t('pricing.crypto_e_feat4'), t('pricing.crypto_e_feat5')], cta: t('pricing.crypto_e_cta'), popular: false },
  ];

  const fiatPlans = [
    { name: 'Starter', icon: Banknote, price: '3.5%', description: t('pricing.per_conversion'), highlight: '24-48h', highlightLabel: t('pricing.by_country'), features: [t('pricing.fiat_s_feat1'), t('pricing.fiat_s_feat2'), t('pricing.fiat_s_feat3'), t('pricing.fiat_s_feat4')], cta: t('pricing.fiat_s_cta'), popular: false },
    { name: 'Pro', icon: Timer, price: '2.0%', description: t('pricing.per_conversion'), highlight: t('pricing.instant_or_24h'), highlightLabel: t('pricing.by_country'), features: [t('pricing.fiat_p_feat1'), t('pricing.fiat_p_feat2'), t('pricing.fiat_p_feat3'), t('pricing.fiat_p_feat4')], cta: t('pricing.fiat_p_cta'), popular: true },
    { name: 'Custom', icon: HeadphonesIcon, price: t('pricing.custom_price'), description: t('pricing.high_volumes'), highlight: t('pricing.instant'), highlightLabel: t('pricing.priority'), features: [t('pricing.fiat_c_feat1'), t('pricing.fiat_c_feat2'), t('pricing.fiat_c_feat3'), t('pricing.fiat_c_feat4')], cta: t('pricing.fiat_c_cta'), popular: false },
  ];

  return (
    <section id="pricing" data-testid="pricing-section" className="py-14 sm:py-20 lg:py-24 relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, hsl(258 26% 8%) 0%, hsl(262 30% 12%) 50%, hsl(258 26% 8%) 100%)' }}>
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-1/4 left-1/3 w-96 h-96 rounded-full bg-[hsl(var(--primary))] opacity-[0.08] blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-[hsl(var(--neon-accent))] opacity-[0.06] blur-[100px]" />
      </div>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-6">
          <Badge variant="outline" className="mb-4 border-[hsl(var(--neon-accent)/0.5)] text-[hsl(var(--neon-accent))] bg-[hsl(var(--neon-accent)/0.15)] shadow-[0_0_15px_hsl(var(--neon-accent)/0.2)]">
            <Sparkles className="w-3 h-3 mr-1.5" /> {t('pricing.badge')}
          </Badge>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-5">
            <span className="text-gradient text-neon drop-shadow-[0_0_30px_hsl(var(--primary-glow)/0.6)]">{t('pricing.title')}</span>
          </h2>
          <p className="text-white/90 max-w-2xl mx-auto text-lg sm:text-xl font-semibold mb-2">{t('pricing.subtitle_main')}</p>
          <p className="text-white/70 max-w-xl mx-auto text-sm sm:text-base">{t('pricing.subtitle_desc')}</p>
        </div>
        <div className="flex items-center justify-center gap-2 sm:gap-4 mb-14 mt-8 px-2">
          <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-2 sm:py-2.5 rounded-xl bg-[hsl(var(--primary)/0.15)] border border-[hsl(var(--primary)/0.3)]">
            <Wallet className="w-4 h-4 sm:w-5 sm:h-5 text-[hsl(var(--primary-glow))]" />
            <span className="text-xs sm:text-sm font-semibold text-white/90">{t('pricing.flow_crypto')}</span>
          </div>
          <div className="flex items-center shrink-0">
            <div className="w-5 sm:w-12 h-px bg-gradient-to-r from-[hsl(var(--primary-glow))] to-[hsl(var(--neon-accent))]" />
            <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[hsl(var(--neon-accent))] -ml-0.5" />
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-2 sm:py-2.5 rounded-xl bg-gradient-to-r from-[hsl(var(--primary)/0.2)] to-[hsl(var(--neon-accent)/0.2)] border border-[hsl(var(--neon-accent)/0.3)] shadow-[0_0_20px_hsl(var(--primary)/0.15)]">
            <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5 text-[hsl(var(--neon-accent))]" />
            <span className="text-xs sm:text-sm font-bold text-white">FluxiPay</span>
          </div>
          <div className="flex items-center shrink-0">
            <div className="w-5 sm:w-12 h-px bg-gradient-to-r from-[hsl(var(--neon-accent))] to-[hsl(var(--success))]" />
            <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[hsl(var(--success))] -ml-0.5" />
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-2 sm:py-2.5 rounded-xl bg-[hsl(var(--success)/0.12)] border border-[hsl(var(--success)/0.3)]">
            <Banknote className="w-4 h-4 sm:w-5 sm:h-5 text-[hsl(var(--success))]" />
            <span className="text-xs sm:text-sm font-semibold text-white/90 whitespace-nowrap">{t('pricing.flow_your_currency')}</span>
          </div>
        </div>
        <div className="mb-20">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg bg-[hsl(var(--primary)/0.2)] flex items-center justify-center">
              <ArrowDownToLine className="w-5 h-5 text-[hsl(var(--primary-glow))]" />
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-white">{t('pricing.crypto_title')}</h3>
              <p className="text-white/65 text-sm">{t('pricing.crypto_subtitle')}</p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {cryptoPlans.map((plan, i) => <PlanCard key={i} plan={plan} index={i} accent="primary" t={t} />)}
          </div>
        </div>
        <div>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg bg-[hsl(var(--neon-accent)/0.2)] flex items-center justify-center">
              <Globe className="w-5 h-5 text-[hsl(var(--neon-accent))]" />
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-white">{t('pricing.fiat_title')}</h3>
              <p className="text-white/65 text-sm">{t('pricing.fiat_subtitle')}</p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {fiatPlans.map((plan, i) => <PlanCard key={i} plan={plan} index={i} accent="accent" t={t} />)}
          </div>
          <div className="mt-8 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-xl bg-[hsl(var(--surface)/0.8)] border border-[hsl(var(--border)/0.4)]">
              <Wallet className="w-5 h-5 text-[hsl(var(--primary-glow))]" />
              <span className="text-sm text-white/80">{t('pricing.crypto_direct_note')}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
